/** @format */

import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Table } from 'antd';
import { useGlobalPerformance } from '../hooks/usePerformance';
import {
  PerformanceFilterInput,
  GlobalPerformanceItem,
} from '../types/performance.types';
import { createGlobalVendorsColumns } from './GlobalVendorsCommonTable';

const GlobalVendorsTable: React.FC = () => {
  const startDate = useSelector((state: any) => state.dropDown?.startDate);
  const endDate = useSelector((state: any) => state.dropDown?.endDate);
  const { data, loading, error, refetch } = useGlobalPerformance();

  useEffect(() => {
    const filter: PerformanceFilterInput = {};

    if (startDate) filter.startDate = startDate;
    if (endDate) filter.endDate = endDate;

    refetch(filter);
  }, [startDate, endDate, refetch]);

  // Debug: Log raw data to see duplicates
  useEffect(() => {
    if (data && data.length > 0) {
      console.log('GlobalVendorsTable RAW DATA:', data);
      // Find Mozilla entries specifically
      const mozillaEntries = data.filter((item: any) => item.globalName === 'Mozilla');
      console.log('Mozilla entries:', mozillaEntries);
      mozillaEntries.forEach((item: any, idx: number) => {
        console.log(`Mozilla ${idx}:`, 'globalId type:', typeof item.globalId, 'globalId:', item.globalId);
      });
    }
  }, [data]);

  const backgroundColors = [
    '#95A4FC',
    '#A1E3CB',
    '#BAEDBD',
    '#B1E3FF',
    '#A8C5DA',
    '#8BC34A',
    '#FFE999',
    '#C6C7F8',
    '#C3A6C6',
    '#FFCB83',
    '#FFC107',
    '#FF5722',
    '#9C27B0',
    '#FF9800',
    '#00BCD4',
  ];

  // Normalize globalId and aggregate duplicates
  const normalizedData = data.reduce((acc: any[], item: any) => {
    // Normalize globalId - extract string if it's an object
    let normalizedGlobalId: string;
    if (typeof item.globalId === 'string') {
      normalizedGlobalId = item.globalId.trim();
    } else if (item.globalId?._id) {
      normalizedGlobalId = String(item.globalId._id).trim();
    } else if (typeof item.globalId === 'object' && item.globalId !== null) {
      normalizedGlobalId = String(item.globalId).trim();
    } else {
      normalizedGlobalId = '';
    }

    // Skip items without a valid global name or globalId
    if (!item.globalName || item.globalName.trim() === '' || !normalizedGlobalId) {
      return acc;
    }

    // Find existing entry with same globalId
    const existingIndex = acc.findIndex(
      (existing) => existing.globalId === normalizedGlobalId
    );

    console.log('GlobalVendorsTable -', item.globalName, 'normalizedId:', normalizedGlobalId, 'existingIndex:', existingIndex, 'acc globalIds:', acc.map(a => a.globalId));

    if (existingIndex >= 0) {
      // Merge with existing entry by summing numeric values
      const existing = acc[existingIndex];
      acc[existingIndex] = {
        ...existing,
        globalId: normalizedGlobalId,
        totalLeads: (existing.totalLeads || 0) + (item.totalLeads || 0),
        totalBillableLeads:
          (existing.totalBillableLeads || 0) + (item.totalBillableLeads || 0),
        totalCost: (existing.totalCost || 0) + (item.totalCost || 0),
        totalACASales:
          (existing.totalACASales || 0) + (item.totalACASales || 0),
        totalU65Sales:
          (existing.totalU65Sales || 0) + (item.totalU65Sales || 0),
        totalPrivateMedPremium:
          (existing.totalPrivateMedPremium || 0) +
          (item.totalPrivateMedPremium || 0),
        totalAncillaries:
          (existing.totalAncillaries || 0) + (item.totalAncillaries || 0),
        totalAncillariesPremium:
          (existing.totalAncillariesPremium || 0) +
          (item.totalAncillariesPremium || 0),
        totalCallLength:
          (existing.totalCallLength || 0) + (item.totalCallLength || 0),
      };
    } else {
      // Add new entry
      acc.push({
        ...item,
        globalId: normalizedGlobalId,
      });
    }

    return acc;
  }, []);

  // Add colors to data for chart visualization
  const dataWithColors = normalizedData.map(
    (item: GlobalPerformanceItem, index: number) => ({
      ...item,
      color: backgroundColors[index % backgroundColors.length],
    })
  );

  const columns = createGlobalVendorsColumns(false);

  if (error) {
    return (
      <div style={{ color: 'red', padding: '20px' }}>
        Error loading data: {error.message}
      </div>
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={dataWithColors}
      rowKey={(record) => record.globalId}
      loading={loading}
      pagination={{
        pageSize: 50,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
      }}
      scroll={{ x: 1200 }}
    />
  );
};

export default GlobalVendorsTable;
